import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLocal, setShowLocal] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(true); // optimistic; only flipped to false if backend explicitly says so
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/auth/config`)
      .then(({ data }) => {
        if (!cancelled && data && typeof data.microsoft_sso_enabled === 'boolean') {
          setSsoEnabled(data.microsoft_sso_enabled);
        }
      })
      .catch(() => {
        // If the config endpoint can't be reached, keep the SSO button visible.
        // A 404 doesn't necessarily mean SSO is off; failing closed would hide a working feature.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
      return detail
        .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${API_URL}/api/auth/microsoft/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full">
        <div className="flex flex-col items-center mb-8">
          <Logo size="xl" />
          <h1
            className="mt-6 text-2xl font-light text-[#2C302E]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            TMC Surveys
          </h1>
          <p className="text-sm text-[#5C635A] mt-1">
            Surveys Made Simple. Insights Made Powerful.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E0D8]">
          {error && (
            <div
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          {ssoEnabled && (
            <button
              type="button"
              onClick={handleMicrosoftLogin}
              className="w-full py-3 px-4 bg-white hover:bg-[#F4F1EA] text-[#2C302E] border border-[#E5E0D8] rounded-full font-medium transition-all duration-200 flex items-center justify-center gap-3"
              data-testid="microsoft-sso-btn"
            >
              <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              Sign in with Microsoft
            </button>
          )}

          {ssoEnabled && (
            <div className="my-5 flex items-center gap-3 text-xs text-[#8E958C]">
              <div className="flex-1 h-px bg-[#E5E0D8]" />
              <span>or</span>
              <div className="flex-1 h-px bg-[#E5E0D8]" />
            </div>
          )}

          {!showLocal ? (
            <button
              type="button"
              onClick={() => setShowLocal(true)}
              className="w-full text-sm text-[#5C635A] hover:text-[#2C302E] transition-colors py-2"
              data-testid="show-local-login-btn"
            >
              Use email & password instead
            </button>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#2C302E] mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E0D8] bg-[#FDFBF7] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 focus:border-[#7A8B7A]"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#2C302E] mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E0D8] bg-[#FDFBF7] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 focus:border-[#7A8B7A]"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="login-submit"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : null}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-[#8E958C] mt-6">
          © {new Date().getFullYear()} TMC Surveys
        </p>
      </div>
    </div>
  );
};

export default Login;
